import DataGrid, { Column, Editing, Paging, SearchPanel } from 'devextreme-react/data-grid';
import SelectBox from 'devextreme-react/select-box';
import TagBox from 'devextreme-react/tag-box';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Card, CardBody, Input, Label } from 'reactstrap';
import { useAuth } from '../../../auth';
import Breadcrumb from '../../../components/common/breadcrumb';
import { getCompanies, getPartyScopes, saveCompany, savePartyScope } from '../../devir-teslim/api';

// Tanımlamalar > Firmalar: every company the system works with — compound / port / PDI / workshop operators, carriers,
// factories (custody_management.party). A company can have several roles. Site operators (Depo Yönetimi > Saha ve
// Operatörler) and each user's company (Kullanıcı Yönetimi) are chosen from this list; a handover only accepts a company
// whose role fits that side of the point. Dealers come from the dealer master data and are hidden unless asked.
// Role group 'Tanımlamalar - Firmalar' (031_user_company.sql).
const ROLE_GROUP = 37;

// Veri kapsamı (row 210): which cars a company's users see on the scoped screens (Şase Sorgulama, Stok İzleme, Raporlar,
// devir-teslim …). Customers by brand (two companies may share a brand); operators the cars at the sites / zones they
// operate (live) and their custody history; carriers what was loaded on them. Users with the role veri_tum_firmalar see
// everything; a user's own brand selection narrows the company's brands.
const VeriKapsami = ({ canEdit }) => {
    const [md, setMd] = useState({ scopes: [], brands: [] });
    const load = useCallback(() => getPartyScopes().then(setMd).catch((e) => toast.error(e?.response?.data?.error || 'Veri kapsamı alınamadı')), []);
    useEffect(() => { load(); }, [load]);
    const persist = (row) => savePartyScope(row)
        .then(() => { toast.success('Kaydedildi'); load(); return false; })
        .catch((e) => { toast.error(e?.response?.data?.error || 'Kaydedilemedi'); return true; });
    const brandName = (id) => md.brands.find((b) => b.id === id)?.name || id;
    return (
        <Card><CardBody>
            <h6>Veri kapsamı</h6>
            <div className="text-muted mb-2">Firmanın kullanıcıları Şase Sorgulama, Stok İzleme, Raporlar ve devir-teslim ekranlarında yalnızca buradaki
                araçları görür. <b>Markalar</b>: müşteri (iki firma aynı markayı görebilir). <b>İşlettiği sahalar</b>: sahalarında / bölgelerinde şu an duran
                araçlar. <b>Kendi geçmişi</b>: devir kaydında taraf olduğu araçlar. <b>Taşıdıkları</b>: grupajlarına veya gemilerinin seferlerine yüklenenler.
                "Tüm firmaların verisi" rolü olan kullanıcıya kapsam uygulanmaz; kullanıcının kendi marka seçimi firmanın markalarını daraltır.</div>
            <DataGrid dataSource={md.scopes} keyExpr="party_id" showBorders columnAutoWidth wordWrapEnabled
                onRowUpdating={(e) => { e.cancel = persist({ ...e.oldData, ...e.newData }); }}>
                <Editing mode="row" allowUpdating={canEdit} />
                <Paging pageSize={20} />
                <SearchPanel visible />
                <Column dataField="party_name" caption="Firma" allowEditing={false} />
                <Column caption="Roller" allowEditing={false} allowSearch dataType="string" calculateCellValue={(r) => (r.party_types || []).join(', ')} />
                {/* names as the cell value so the search finds brands; the ids are written back on edit */}
                <Column caption="Markalar" name="brands" allowSearch dataType="string" calculateCellValue={(r) => (r.brand_ids || []).map(brandName).join(', ')}
                    setCellValue={(newData, value) => { newData.brand_ids = value; }}
                    editCellRender={(c) => (
                        <TagBox dataSource={md.brands} valueExpr="id" displayExpr="name" defaultValue={c.data.brand_ids || []}
                            showSelectionControls applyValueMode="useButtons" onValueChanged={(e) => c.setValue(e.value)} />
                    )} />
                <Column dataField="operated_sites" caption="İşlettiği sahalar (canlı)" dataType="boolean" />
                <Column dataField="custody_history" caption="Kendi geçmişi" dataType="boolean" />
                <Column dataField="carrier_loads" caption="Taşıdıkları" dataType="boolean" />
                <Column dataField="users" caption="Kullanıcı" dataType="number" allowEditing={false} width={90} />
            </DataGrid>
        </CardBody></Card>
    );
};

const Firmalar = () => {
    const { hasRole } = useAuth(ROLE_GROUP);
    const canEdit = hasRole('firma_write');
    const [md, setMd] = useState({ parties: [], party_types: [], subcontractors: [] });
    const [dealers, setDealers] = useState(false);
    const load = useCallback(() => getCompanies().then(setMd).catch((e) => toast.error(e?.response?.data?.error || 'Firmalar alınamadı')), []);
    useEffect(() => { load(); }, [load]);
    const persist = (key, values) => saveCompany(key, values)
        .then(() => { toast.success('Kaydedildi'); load(); return false; })
        .catch((e) => { toast.error(e?.response?.data?.error || 'Kaydedilemedi'); return true; });
    const rows = md.parties.filter((p) => dealers || !p.party_types.includes('BAYI'));
    const types = md.party_types.filter((t) => t.is_active);
    const typeName = (c) => md.party_types.find((x) => x.code === c)?.name_tr || c;
    return (
        <>
            <Breadcrumb parent="Tanımlamalar" current="Firmalar" />
            <Card><CardBody>
                <div className="d-flex align-items-center mb-2" style={{ gap: 16 }}>
                    <span className="text-muted">Operatörler, taşıyıcılar, limanlar, fabrikalar… Bir firma birden fazla rol alabilir (ör. CAR Concept: PDI + Atölye).
                        Saha operatörleri ve kullanıcıların firması bu listeden seçilir.</span>
                    <Label className="mb-0 ml-auto text-nowrap"><Input type="checkbox" checked={dealers} onChange={(e) => setDealers(e.target.checked)} /> Bayileri de göster</Label>
                </div>
                <DataGrid dataSource={rows} keyExpr="id" showBorders columnAutoWidth wordWrapEnabled
                    onRowUpdating={(e) => { e.cancel = persist(e.key, e.newData); }}
                    onRowInserting={(e) => { const { id, ...values } = e.data; e.cancel = persist(0, { is_active: true, party_types: [], ...values }); }}>
                    <Editing mode="row" allowUpdating={canEdit} allowAdding={canEdit} />
                    <Paging pageSize={25} />
                    <SearchPanel visible />
                    <Column dataField="name" caption="Firma" />
                    <Column dataField="party_types" caption="Roller"
                        cellRender={(c) => (c.value || []).map(typeName).join(', ')}
                        editCellRender={(c) => (
                            <TagBox dataSource={types} valueExpr="code" displayExpr="name_tr" defaultValue={c.value || []}
                                showSelectionControls applyValueMode="useButtons" onValueChanged={(e) => c.setValue(e.value)} />
                        )} />
                    <Column dataField="country" caption="Ülke" width={90} />
                    <Column dataField="subcontractor_id" caption="Taşeron (tır eşleme)" width={180}
                        cellRender={(c) => md.subcontractors.find((s) => s.id === c.value)?.name || ''}
                        editCellRender={(c) => (
                            <SelectBox dataSource={md.subcontractors} valueExpr="id" displayExpr="name" showClearButton
                                searchEnabled defaultValue={c.value} onValueChanged={(e) => c.setValue(e.value)} />
                        )} />
                    <Column dataField="is_active" caption="Aktif" dataType="boolean" width={80} />
                </DataGrid>
            </CardBody></Card>
            <VeriKapsami canEdit={canEdit} />
        </>
    );
};

export default Firmalar;
